import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { ExternalLink, Check, X } from 'lucide-react';

const AdminVerificationPage = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    adminService.listPendingVerifications().then((data) => {
      setPending(data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleView = async (filePath) => {
    const url = await adminService.getVerificationDocumentSignedUrl(filePath);
    if (url) window.open(url, '_blank');
  };

  const handleApprove = async (docId) => {
    setActing(docId);
    try {
      await adminService.approveVerificationDocument(docId);
      setPending((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      console.error(err);
    }
    setActing(null);
  };

  const handleReject = async (docId) => {
    setActing(docId);
    try {
      await adminService.rejectVerificationDocument(docId);
      setPending((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      console.error(err);
    }
    setActing(null);
  };

  const displayName = (row) => {
    const up = row.user_profiles ?? row.user_profiles_verification_documents_user_id_fkey;
    return up?.display_name ?? up?.email ?? row.user_id ?? '—';
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">Verification</h1>
      <p className="text-slate-600 mb-6">Review pending ID documents and approve or reject.</p>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
        </div>
      ) : pending.length === 0 ? (
        <div className="p-8 rounded-xl bg-white border border-slate-200 text-center text-slate-500">
          No pending verifications.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-medium text-slate-700">User</th>
                <th className="text-left p-3 font-medium text-slate-700">Type</th>
                <th className="text-left p-3 font-medium text-slate-700">Submitted</th>
                <th className="text-left p-3 font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((doc) => (
                <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3">{displayName(doc)}</td>
                  <td className="p-3">{doc.document_type || 'id'}</td>
                  <td className="p-3">{doc.created_at ? new Date(doc.created_at).toLocaleString() : '—'}</td>
                  <td className="p-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleView(doc.file_path)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApprove(doc.id)}
                      disabled={acting === doc.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Check className="h-4 w-4" />
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReject(doc.id)}
                      disabled={acting === doc.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminVerificationPage;
